import React from "react";
import WebProfitSpinner from "../../system/WebProfitSpinner";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import { erpApi } from "../../../api/erp";
import { ERP_BRAND } from "../../../utils/erp";
import { resolveProductImageUrl } from "../../../utils/productMedia";
import { reportActiveDashboardTutorialMetric } from "../../../utils/dashboardTutorial";

const FILTERS = [
  { value: "all", label: "Todos" },
  { value: "pending", label: "Pendientes" },
  { value: "counted", label: "Contados" },
];

function quantityText(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
}

function signed(value) {
  const number = Number(value || 0);
  if (!number) return "0";
  return number > 0 ? `+${number}` : String(number);
}

function money(value, currency = "ARS") {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(value));
  } catch {
    return `${currency} ${Number(value).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
  }
}

function SaveState({ pendingCount, errors }) {
  if (Object.keys(errors).length) return <Chip size="small" color="error" label="Cambios sin guardar" />;
  if (pendingCount > 0) return <Chip size="small" icon={<WebProfitSpinner size={13} />} label="Guardando…" />;
  return <Chip size="small" color="success" variant="outlined" label="Guardado" />;
}

function CountPagination({ page, pageSize = 50, total, loading, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(Number(total || 0) / pageSize));
  if (totalPages <= 1) return null;

  const go = (nextPage) => {
    const safePage = Math.min(totalPages, Math.max(1, Number(nextPage || 1)));
    if (safePage !== page) onPageChange(safePage);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={0.6}
      sx={{ py: 0.35, minHeight: 36 }}
    >
      <Stack direction="row" spacing={0.35}>
        <Button size="small" disabled={page <= 1 || loading} onClick={() => go(1)} sx={{ minWidth: 0, px: 0.8, textTransform: "none" }}>Primera</Button>
        <Button size="small" disabled={page <= 1 || loading} onClick={() => go(page - 1)} sx={{ minWidth: 0, px: 0.8, textTransform: "none" }}>Anterior</Button>
      </Stack>
      <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850, whiteSpace: "nowrap" }}>
        Página {page} de {totalPages} · {Number(total || 0)} ítems
      </Typography>
      <Stack direction="row" spacing={0.35}>
        <Button size="small" disabled={page >= totalPages || loading} onClick={() => go(page + 1)} sx={{ minWidth: 0, px: 0.8, textTransform: "none" }}>Siguiente</Button>
        <Button size="small" disabled={page >= totalPages || loading} onClick={() => go(totalPages)} sx={{ minWidth: 0, px: 0.8, textTransform: "none" }}>Última</Button>
      </Stack>
    </Stack>
  );
}

function CountLineRow({ line, inputValue, onInputChange, onInputBlur, onIncrement, onDecrement, onPending, onNext, inputRef }) {
  const counted = line.countedQuantity !== null && line.countedQuantity !== undefined;
  const image = resolveProductImageUrl(line.imagePathSnapshot || "");
  const variantLabel = String(line.variantLabelSnapshot || "").trim();
  const skuOrCode = String(line.variantSkuSnapshot || line.codeSnapshot || "").trim();

  // Hotfix status: REVIEWED — presentation-only; stock/movement mutation paths stay untouched.
  // Test contract: desktop gets explicit variant/SKU columns; mobile keeps the same
  // identity below the item name so a counted line never becomes ambiguous.
  const mobileDescriptor = [variantLabel, skuOrCode].filter(Boolean).join(" · ");

  return (
    <TableRow
      hover
      sx={{
        bgcolor: counted ? "rgba(39,174,96,.025)" : "transparent",
        "& > .MuiTableCell-root": { borderColor: ERP_BRAND.border },
      }}
    >
      <TableCell sx={{ py: 0.8, pl: { xs: 0.6, sm: 1.25 }, pr: 0.8 }}>
        <Stack direction="row" spacing={0.9} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            variant="rounded"
            src={image || undefined}
            sx={{ width: 36, height: 36, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.05)", color: ERP_BRAND.muted, flexShrink: 0 }}
          >
            <InventoryRoundedIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 900, color: ERP_BRAND.dark, fontSize: 13.5, lineHeight: 1.2 }}>
              {line.productNameSnapshot}
            </Typography>
            {mobileDescriptor ? (
              <Typography
                variant="caption"
                sx={{
                  color: ERP_BRAND.muted,
                  display: { xs: "block", md: "none" },
                  maxWidth: 190,
                  lineHeight: 1.2,
                  overflowWrap: "anywhere",
                }}
              >
                {mobileDescriptor}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </TableCell>
      <TableCell sx={{ py: 0.8, display: { xs: "none", md: "table-cell" }, width: 210 }}>
        <Typography
          variant="body2"
          sx={{
            color: variantLabel ? ERP_BRAND.dark : ERP_BRAND.muted,
            fontWeight: variantLabel ? 850 : 650,
            lineHeight: 1.2,
            overflowWrap: "anywhere",
          }}
        >
          {variantLabel || "Sin variante"}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 0.8, display: { xs: "none", md: "table-cell" }, width: 145 }}>
        <Typography variant="caption" sx={{ color: skuOrCode ? ERP_BRAND.dark : ERP_BRAND.muted, fontWeight: skuOrCode ? 800 : 600 }}>
          {skuOrCode || "—"}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 0.8, display: { xs: "none", md: "table-cell" }, width: 160 }}>
        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{line.categorySnapshot || "Sin categoría"}</Typography>
      </TableCell>
      <TableCell align="center" sx={{ py: 0.8, display: { xs: "none", sm: "table-cell" }, width: 110 }}>
        <Chip
          size="small"
          variant={counted ? "outlined" : "filled"}
          color={counted ? "success" : "default"}
          label={counted ? "Contado" : "Pendiente"}
          sx={{ fontWeight: 800 }}
        />
      </TableCell>
      <TableCell align="right" sx={{ py: 0.65, pl: 0.5, pr: { xs: 0.5, sm: 1.25 }, width: { xs: 164, sm: 230 } }}>
        <Stack direction="row" spacing={0.45} alignItems="center" justifyContent="flex-end">
          <IconButton
            size="small"
            aria-label={`Restar cantidad de ${line.productNameSnapshot}${line.variantLabelSnapshot ? ` ${line.variantLabelSnapshot}` : ""}`}
            onClick={onDecrement}
            sx={{ width: 32, height: 32, border: `1px solid ${ERP_BRAND.border}`, borderRadius: 1.6 }}
          >
            <RemoveRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <TextField
            inputRef={inputRef}
            value={inputValue}
            onChange={(event) => onInputChange(event.target.value)}
            onBlur={onInputBlur}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onNext();
              }
            }}
            size="small"
            type="number"
            inputProps={{ min: 0, step: "any", inputMode: "decimal", "aria-label": `Cantidad encontrada de ${line.productNameSnapshot}` }}
            sx={{
              width: { xs: 62, sm: 76 },
              "& .MuiInputBase-root": { height: 34, borderRadius: 1.6 },
              "& input": { textAlign: "center", px: 0.4, py: 0.7, fontSize: 15, fontWeight: 950 },
            }}
          />
          <IconButton
            size="small"
            aria-label={`Sumar cantidad de ${line.productNameSnapshot}${line.variantLabelSnapshot ? ` ${line.variantLabelSnapshot}` : ""}`}
            onClick={onIncrement}
            sx={{ width: 32, height: 32, border: `1px solid ${ERP_BRAND.border}`, borderRadius: 1.6 }}
          >
            <AddRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Button
            onClick={onPending}
            size="small"
            disabled={!counted && inputValue === ""}
            sx={{
              minWidth: { xs: 30, sm: 72 },
              width: { xs: 30, sm: "auto" },
              px: { xs: 0, sm: 0.8 },
              textTransform: "none",
              fontWeight: 850,
              color: ERP_BRAND.muted,
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Pendiente</Box>
            <Box component="span" sx={{ display: { xs: "inline", sm: "none" }, fontSize: 18, lineHeight: 1 }}>×</Box>
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function ReviewRow({ line, onOmit, disabled }) {
  const history = line.historicalReconciliation || null;
  return (
    <Box sx={{ border: `1px solid ${line.blocking ? "rgba(211,47,47,.35)" : ERP_BRAND.border}`, borderRadius: 3, p: 1.4, bgcolor: "var(--wp-panel-surface, #fff)" }}>
      <Stack spacing={0.7}>
        <Box>
          <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{line.productNameSnapshot}</Typography>
          {line.variantLabelSnapshot || line.variantSkuSnapshot ? (
            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
              {[line.variantLabelSnapshot, line.variantSkuSnapshot].filter(Boolean).join(" · ")}
            </Typography>
          ) : null}
        </Box>
        {line.counted ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.2, sm: 2 }}>
            <Typography variant="body2">Referencia al contar: <b>{line.stockReference}</b></Typography>
            <Typography variant="body2">Encontrado: <b>{line.countedQuantity}</b></Typography>
            <Typography variant="body2">Diferencia: <b>{signed(line.delta)}</b></Typography>
          </Stack>
        ) : <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>No contado todavía</Typography>}
        {history && line.counted ? (
          <Box sx={{ bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)", borderRadius: 2, px: 1.1, py: 0.8 }}>
            <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, fontWeight: 850 }}>Conciliación histórica</Typography>
            <Typography variant="body2" sx={{ color: ERP_BRAND.dark }}>
              Comprado: <b>{history.purchaseQuantity}</b> · ventas históricas registradas: <b>{history.knownSaleQuantity}</b> · stock contado: <b>{history.countedQuantity}</b>
            </Typography>
            {Number(history.unexplainedOutflowQuantity || 0) > 0 ? (
              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                Diferencia sin documento: <b>{history.unexplainedOutflowQuantity}</b> unidades. Puede ser venta no cargada, consumo, merma o una existencia previa incompleta.
              </Typography>
            ) : Number(history.stockBeyondHistoryQuantity || 0) > 0 ? (
              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                Hay <b>{history.stockBeyondHistoryQuantity}</b> unidades más que las explicadas por las facturas históricas; probablemente falta historia anterior o compras por cargar.
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>El conteo queda explicado por los documentos históricos cargados.</Typography>
            )}
          </Box>
        ) : null}
        {line.movedAfterCount ? (
          <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
            Stock actual: <b>{line.currentStock}</b> · luego del ajuste quedaría en <b>{line.projectedStock}</b>.
          </Typography>
        ) : null}
        {line.warning ? <Alert severity={line.blocking ? "error" : "warning"} sx={{ py: 0 }}>{line.warning}</Alert> : null}
        {line.blocking ? (
          <Button onClick={onOmit} disabled={disabled} size="small" variant="outlined" sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>
            Omitir y dejar pendiente
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function InitialStockCountDialog({
  open,
  onClose,
  projectId,
  activeCount,
  canConfirm,
  onChanged,
  onGoItems,
  onGoMovements,
}) {
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const [mode, setMode] = React.useState("intro");
  const [session, setSession] = React.useState(null);
  const [summary, setSummary] = React.useState(null);
  const [lines, setLines] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("pending");
  const [sortDirection, setSortDirection] = React.useState("desc");
  const [page, setPage] = React.useState(1);
  const [totalFiltered, setTotalFiltered] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [quantityInputs, setQuantityInputs] = React.useState({});
  const [saveErrors, setSaveErrors] = React.useState({});
  const [pendingSaveCount, setPendingSaveCount] = React.useState(0);
  const [review, setReview] = React.useState(null);
  const [reviewScope, setReviewScope] = React.useState("differences");
  const [reviewPage, setReviewPage] = React.useState(1);
  const [confirming, setConfirming] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const queuesRef = React.useRef(new Map());
  const inputRefs = React.useRef(new Map());
  const focusFirstRef = React.useRef(false);
  const wasOpenRef = React.useRef(false);
  // BLOCK 1 · sample physical-work progress without changing save/confirm semantics.
  // STATUS: STATIC_PASS · runtime count/resume/confirm scenarios pending staging.
  const telemetryProgressRef = React.useRef({ sessionId: "", bucket: -1, counted: -1 });

  React.useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 280);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    const warn = (event) => {
      if (!open || (pendingSaveCount === 0 && !Object.keys(saveErrors).length)) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [open, pendingSaveCount, saveErrors]);

  const syncPendingSaveCount = React.useCallback(() => {
    setPendingSaveCount([...queuesRef.current.values()].filter((entry) => entry.inFlight || entry.dirtyValue !== undefined).length);
  }, []);

  const mergeLine = React.useCallback((serverLine, sentValue) => {
    setLines((current) => current.map((line) => {
      if (line.id !== serverLine.id) return line;
      const queue = queuesRef.current.get(line.id);
      const newerLocalValue = queue?.dirtyValue;
      return {
        ...line,
        ...serverLine,
        ...(newerLocalValue !== undefined && newerLocalValue !== sentValue ? { countedQuantity: newerLocalValue } : {}),
      };
    }));
  }, []);

  const flushLine = React.useCallback(async (lineId) => {
    const entry = queuesRef.current.get(lineId);
    if (!entry || entry.inFlight || entry.dirtyValue === undefined || !session?.id) return entry?.promise;
    if (entry.timer) window.clearTimeout(entry.timer);
    const sentValue = entry.dirtyValue;
    entry.dirtyValue = undefined;
    entry.inFlight = true;
    entry.error = null;
    syncPendingSaveCount();
    setSaveErrors((current) => {
      const next = { ...current };
      delete next[lineId];
      return next;
    });

    entry.promise = erpApi.updateStockCountLine(projectId, session.id, lineId, sentValue)
      .then((data) => {
        mergeLine(data.line, sentValue);
        setSummary(data.summary || null);
      })
      .catch((err) => {
        const message = err.message || "No se pudo guardar";
        entry.error = message;
        entry.dirtyValue = entry.dirtyValue === undefined ? sentValue : entry.dirtyValue;
        setSaveErrors((current) => ({ ...current, [lineId]: message }));
      })
      .finally(() => {
        entry.inFlight = false;
        syncPendingSaveCount();
        if (entry.dirtyValue !== undefined && !entry.error) {
          entry.timer = window.setTimeout(() => flushLine(lineId), 350);
        }
      });
    return entry.promise;
  }, [mergeLine, projectId, session?.id, syncPendingSaveCount]);

  const queueSave = React.useCallback((lineId, value) => {
    let entry = queuesRef.current.get(lineId);
    if (!entry) {
      entry = { dirtyValue: undefined, inFlight: false, timer: null, promise: null, error: null };
      queuesRef.current.set(lineId, entry);
    }
    entry.dirtyValue = value;
    entry.error = null;
    setSaveErrors((current) => {
      const next = { ...current };
      delete next[lineId];
      return next;
    });
    syncPendingSaveCount();
    if (entry.timer) window.clearTimeout(entry.timer);
    entry.timer = window.setTimeout(() => flushLine(lineId), 420);
  }, [flushLine, syncPendingSaveCount]);

  const flushAll = React.useCallback(async () => {
    const ids = [...queuesRef.current.keys()];
    for (const lineId of ids) {
      const entry = queuesRef.current.get(lineId);
      if (entry?.timer) window.clearTimeout(entry.timer);
      if (entry?.inFlight && entry.promise) await entry.promise;
      if (entry?.dirtyValue !== undefined) await flushLine(lineId);
      const latest = queuesRef.current.get(lineId);
      if (latest?.inFlight && latest.promise) await latest.promise;
    }
    syncPendingSaveCount();
    return [...queuesRef.current.values()].every((entry) => !entry.inFlight && entry.dirtyValue === undefined && !entry.error);
  }, [flushLine, syncPendingSaveCount]);

  const loadLines = React.useCallback(async (countId = session?.id) => {
    if (!countId) return;
    setLoading(true);
    setError("");
    try {
      const data = await erpApi.getStockCount(projectId, countId, {
        page,
        pageSize: 50,
        q: search,
        category,
        status: statusFilter,
        order: sortDirection,
      });
      setSession(data.session);
      setSummary(data.summary);
      setCategories(data.categories || []);
      setTotalFiltered(Number(data.totalFiltered || 0));
      setHasMore(Boolean(data.hasMore));
      setLines((data.lines || []).map((line) => {
        const queue = queuesRef.current.get(line.id);
        return queue?.dirtyValue !== undefined ? { ...line, countedQuantity: queue.dirtyValue } : line;
      }));
      setQuantityInputs((current) => {
        const next = { ...current };
        for (const line of data.lines || []) {
          const queue = queuesRef.current.get(line.id);
          if (next[line.id] === undefined || queue?.dirtyValue === undefined) next[line.id] = quantityText(line.countedQuantity);
        }
        return next;
      });
      if (data.session?.status === "confirming") {
        setMode("review");
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar el conteo");
    } finally {
      setLoading(false);
    }
  }, [category, page, projectId, search, session?.id, sortDirection, statusFilter]);

  const loadReview = React.useCallback(async (countId = session?.id) => {
    if (!countId) return;
    setLoading(true);
    setError("");
    try {
      const saved = await flushAll();
      if (!saved) {
        setError("Hay cambios que todavía no pudieron guardarse. Reintentá antes de revisar.");
        setMode("work");
        return;
      }
      const data = await erpApi.reviewStockCount(projectId, countId, {
        page: reviewPage,
        pageSize: 50,
        scope: reviewScope,
        order: sortDirection,
      });
      setSession(data.session);
      setSummary(data.summary);
      setReview(data);
    } catch (err) {
      setError(err.message || "No se pudo preparar la revisión");
    } finally {
      setLoading(false);
    }
  }, [flushAll, projectId, reviewPage, reviewScope, session?.id, sortDirection]);

  React.useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;
    setError("");
    setResult(null);
    setReview(null);
    setSortDirection("desc");
    queuesRef.current.clear();
    setSaveErrors({});
    setPendingSaveCount(0);
    telemetryProgressRef.current = { sessionId: "", bucket: -1, counted: -1 };
    if (activeCount?.session?.id) {
      setSession(activeCount.session);
      setSummary(activeCount.summary || null);
      setMode(activeCount.session.status === "confirming" ? "review" : "work");
      setPage(1);
    } else {
      setSession(null);
      setSummary(null);
      setMode("intro");
    }
  }, [activeCount, open]);

  React.useEffect(() => {
    if (!open || mode !== "work" || !session?.id) return;
    loadLines(session.id);
  }, [loadLines, mode, open, session?.id]);

  React.useEffect(() => {
    if (!open || mode !== "review" || !session?.id) return;
    loadReview(session.id);
  }, [loadReview, mode, open, session?.id]);

  React.useEffect(() => {
    if (!focusFirstRef.current || loading || !lines.length) return;
    focusFirstRef.current = false;
    const target = lines.find((line) => line.countedQuantity === null) || lines[0];
    window.setTimeout(() => inputRefs.current.get(target.id)?.focus?.(), 50);
  }, [lines, loading]);

  React.useEffect(() => {
    if (!open || mode !== "work" || !session?.id || !summary) return;
    const counted = Math.max(0, Number(summary.counted || 0));
    const total = Math.max(0, Number(summary.total || 0));
    const bucket = total > 0 ? Math.floor((counted / total) * 10) : Math.floor(counted / 5);
    const previous = telemetryProgressRef.current;
    const sessionChanged = previous.sessionId !== String(session.id);
    const meaningfulAdvance = sessionChanged || bucket > previous.bucket || counted === total;
    if (!meaningfulAdvance || counted === previous.counted) return;
    telemetryProgressRef.current = { sessionId: String(session.id), bucket, counted };
    reportActiveDashboardTutorialMetric("stock_count_progress", {
        operationId: session?.id ? `stock-count:${session.id}` : "",
      sourceType: "physical_count",
      itemCount: total,
      processedCount: counted,
      errorCount: Object.keys(saveErrors).length,
      completionPct: total > 0 ? Math.round((counted / total) * 1000) / 10 : 0,
    });
  }, [mode, open, saveErrors, session?.id, summary]);

  const start = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await erpApi.startStockCount(projectId);
      setSession(data.session);
      setSummary(data.summary);
      reportActiveDashboardTutorialMetric("stock_count_started", {
        operationId: data?.session?.id ? `stock-count:${data.session.id}` : "",
        sourceType: "physical_count",
        itemCount: Number(data?.summary?.total || 0),
        processedCount: Number(data?.summary?.counted || 0),
        errorCount: 0,
      });
      setMode("work");
      setStatusFilter("pending");
      setPage(1);
      onChanged?.();
    } catch (err) {
      setError(err.message || "No se pudo iniciar el conteo");
    } finally {
      setLoading(false);
    }
  };

  const setLineLocal = (lineId, value) => {
    setLines((current) => current.map((line) => line.id === lineId ? { ...line, countedQuantity: value } : line));
    setQuantityInputs((current) => ({ ...current, [lineId]: quantityText(value) }));
    queueSave(lineId, value);
  };

  const inputChange = (line, raw) => {
    setQuantityInputs((current) => ({ ...current, [line.id]: raw }));
    if (raw === "") return;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) return;
    setLines((current) => current.map((item) => item.id === line.id ? { ...item, countedQuantity: value } : item));
    queueSave(line.id, value);
  };

  const changeBy = (line, delta) => {
    const raw = quantityInputs[line.id];
    const current = raw !== "" && Number.isFinite(Number(raw))
      ? Number(raw)
      : line.countedQuantity === null ? 0 : Number(line.countedQuantity || 0);
    const next = Math.max(0, current + delta);
    setLineLocal(line.id, next);
  };

  const goNext = (index) => {
    const next = lines.slice(index + 1).find((line) => line.countedQuantity === null);
    if (next) {
      inputRefs.current.get(next.id)?.focus?.();
      inputRefs.current.get(next.id)?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      return;
    }
    if (hasMore) {
      focusFirstRef.current = true;
      setPage((value) => value + 1);
    }
  };

  const retrySaves = async () => {
    setSaveErrors({});
    for (const entry of queuesRef.current.values()) entry.error = null;
    await flushAll();
  };

  const refreshCatalog = async () => {
    if (!session?.id) return;
    setLoading(true);
    setError("");
    try {
      const data = await erpApi.refreshStockCountCatalog(projectId, session.id);
      setSummary(data.summary);
      setPage(1);
      await loadLines(session.id);
      if (data.added > 0) setError(`Se agregaron ${data.added} existencias nuevas como pendientes.`);
      onChanged?.();
    } catch (err) {
      setError(err.message || "No se pudo actualizar el catálogo del conteo");
    } finally {
      setLoading(false);
    }
  };

  const omitReviewLine = async (line) => {
    setLoading(true);
    setError("");
    try {
      await erpApi.updateStockCountLine(projectId, session.id, line.id, null);
      onChanged?.();
      await loadReview(session.id);
    } catch (err) {
      setError(err.message || "No se pudo dejar la línea pendiente");
    } finally {
      setLoading(false);
    }
  };

  const confirm = async (allowPending) => {
    if (!session?.id || !canConfirm) return;
    setConfirming(true);
    setError("");
    try {
      const data = await erpApi.confirmStockCount(projectId, session.id, { allowPending });
      setResult(data.result);
      reportActiveDashboardTutorialMetric("stock_count_completed", {
        operationId: session?.id ? `stock-count:${session.id}` : "",
        sourceType: "physical_count",
        itemCount: Number(summary?.total || 0),
        processedCount: Number(summary?.counted || 0),
        errorCount: 0,
      });
      setMode("result");
      onChanged?.({ completed: true });
    } catch (err) {
      reportActiveDashboardTutorialMetric("stock_count_failed", {
        operationId: session?.id ? `stock-count:${session.id}` : "",
        sourceType: "physical_count",
        itemCount: Number(summary?.total || 0),
        processedCount: Number(summary?.counted || 0),
        errorCount: 1,
      });
      setError(err.message || "No se pudo confirmar el conteo. Podés reintentar con la misma sesión.");
      await loadReview(session.id).catch(() => {});
    } finally {
      setConfirming(false);
    }
  };

  const discard = async () => {
    if (!session?.id || !window.confirm("¿Descartar este conteo? El stock real no se modificará.")) return;
    setLoading(true);
    setError("");
    for (const entry of queuesRef.current.values()) {
      if (entry.timer) window.clearTimeout(entry.timer);
      entry.timer = null;
      entry.dirtyValue = undefined;
    }
    syncPendingSaveCount();
    try {
      await erpApi.discardStockCount(projectId, session.id);
      reportActiveDashboardTutorialMetric("stock_count_abandoned", {
        operationId: session?.id ? `stock-count:${session.id}` : "",
        sourceType: "physical_count",
        itemCount: Number(summary?.total || 0),
        processedCount: Number(summary?.counted || 0),
        errorCount: Object.keys(saveErrors).length,
      });
      onChanged?.({ discarded: true });
      onClose?.();
    } catch (err) {
      setError(err.message || "No se pudo descartar el conteo");
    } finally {
      setLoading(false);
    }
  };

  const safeClose = async () => {
    const unsaved = pendingSaveCount > 0 || Object.keys(saveErrors).length > 0 || [...queuesRef.current.values()].some((entry) => entry.dirtyValue !== undefined);
    if (unsaved && !window.confirm("Hay cambios que todavía no llegaron al servidor. ¿Salir igualmente?")) return;
    if (unsaved) {
      for (const entry of queuesRef.current.values()) {
        if (entry.timer) window.clearTimeout(entry.timer);
        entry.timer = null;
        entry.dirtyValue = undefined;
      }
      syncPendingSaveCount();
    }
    if (session?.id && !result) {
      reportActiveDashboardTutorialMetric("stock_count_paused", {
        operationId: session?.id ? `stock-count:${session.id}` : "",
        sourceType: "physical_count",
        itemCount: Number(summary?.total || 0),
        processedCount: Number(summary?.counted || 0),
        errorCount: Object.keys(saveErrors).length,
      });
    }
    onChanged?.();
    onClose?.();
  };

  const title = mode === "intro" ? "Conteo inicial" : mode === "review" ? "Revisar conteo" : mode === "result" ? "Conteo confirmado" : "Conteo inicial";

  return (
    <Dialog
      open={open}
      onClose={safeClose}
      fullScreen={mobile}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          width: mobile ? "100%" : "min(1180px, calc(100vw - 32px))",
          maxHeight: mobile ? "100dvh" : "92dvh",
          minHeight: !mobile && mode === "work" ? "78dvh" : undefined,
          borderRadius: mobile ? 0 : 3,
        },
      }}
    >
      <DialogTitle sx={{ py: 1.2, px: { xs: 1.25, sm: 2.5 }, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {mode === "review" && session?.status !== "confirming" ? (
            <IconButton aria-label="Volver al conteo" onClick={() => setMode("work")}><ArrowBackRoundedIcon /></IconButton>
          ) : null}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{title}</Typography>
            {mode === "work" && summary ? (
              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{summary.counted} / {summary.total} contados · {summary.pending} pendientes</Typography>
            ) : null}
          </Box>
          {mode === "work" ? <SaveState pendingCount={pendingSaveCount} errors={saveErrors} /> : null}
          <IconButton aria-label="Cerrar conteo" onClick={safeClose}><CloseRoundedIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.25, sm: 2.5 }, bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", overflowX: "hidden" }}>
        <Stack spacing={1.5}>
          {error ? (
            <Alert severity={error.startsWith("Se agregaron") ? "success" : "error"} onClose={() => setError("")}>
              {error}
              {Object.keys(saveErrors).length ? <Button size="small" onClick={retrySaves} sx={{ ml: 1 }}>Reintentar guardado</Button> : null}
            </Alert>
          ) : null}

          {mode === "intro" ? (
            <Stack spacing={2.2} sx={{ py: { xs: 2, sm: 4 }, maxWidth: 620, mx: "auto" }}>
              <Box sx={{ width: 62, height: 62, borderRadius: 4, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.1)", color: ERP_BRAND.orange }}>
                <InventoryRoundedIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Contá lo que encontrás</Typography>
                <Typography sx={{ color: ERP_BRAND.muted, mt: 0.8 }}>
                  WebProfit guarda el progreso, calcula las diferencias y no cambia tu stock hasta que confirmes al final.
                </Typography>
              </Box>
              <Alert severity="info">Cero significa “lo conté y no encontré unidades”. “No contado todavía” queda pendiente y no modifica nada.</Alert>
              <Button variant="contained" size="large" onClick={start} disabled={loading} startIcon={loading ? <WebProfitSpinner size={17} /> : undefined} sx={{ minHeight: 52, textTransform: "none", fontWeight: 950 }}>
                {loading ? "Preparando conteo…" : "Empezar a contar"}
              </Button>
              <Button variant="text" onClick={onGoItems} disabled={loading} sx={{ textTransform: "none", fontWeight: 850 }}>Ir a Ítems</Button>
            </Stack>
          ) : null}

          {mode === "work" ? (
            <>
              <Box sx={{ position: "sticky", top: -10, zIndex: 3, bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", pt: 0.2, pb: 1 }}>
                <Stack spacing={0.9}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={0.75} alignItems={{ md: "center" }}>
                    <TextField
                      value={searchInput}
                      onChange={(event) => { setSearchInput(event.target.value); setPage(1); }}
                      placeholder="Buscar ítem, código o SKU"
                      size="small"
                      sx={{ flex: 1, minWidth: { md: 280 } }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 19 }} /></InputAdornment> }}
                    />
                    <TextField
                      select
                      size="small"
                      label="Categoría"
                      value={category}
                      onChange={(event) => { setCategory(event.target.value); setPage(1); }}
                      sx={{ minWidth: { xs: "100%", md: 180 } }}
                    >
                      <MenuItem value="">Todas</MenuItem>
                      {categories.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Estado"
                      value={statusFilter}
                      onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}
                      sx={{ minWidth: { xs: "100%", md: 145 } }}
                    >
                      {FILTERS.map((filter) => <MenuItem key={filter.value} value={filter.value}>{filter.label}</MenuItem>)}
                    </TextField>
                    <Button
                      variant="outlined"
                      startIcon={<SwapVertRoundedIcon />}
                      onClick={() => { setSortDirection((value) => value === "desc" ? "asc" : "desc"); setPage(1); }}
                      sx={{ minHeight: 40, px: 1.2, whiteSpace: "nowrap", textTransform: "none", fontWeight: 900 }}
                    >
                      {sortDirection === "desc" ? "Últimos primero" : "Primeros primero"}
                    </Button>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap" alignItems="center">
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850 }}>
                        {totalFiltered} visibles · {summary?.counted || 0}/{summary?.total || 0} contados · {summary?.pending || 0} pendientes
                      </Typography>
                    </Stack>
                    <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={refreshCatalog} disabled={loading} sx={{ textTransform: "none", fontWeight: 850, whiteSpace: "nowrap" }}>
                      Actualizar ítems
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              <CountPagination
                page={page}
                total={totalFiltered}
                loading={loading}
                onPageChange={(nextPage) => { focusFirstRef.current = true; setPage(nextPage); }}
              />

              <TableContainer
                sx={{
                  border: `1px solid ${ERP_BRAND.border}`,
                  borderRadius: 2.2,
                  bgcolor: "var(--wp-panel-surface, #fff)",
                  overflowX: "auto",
                  maxHeight: { xs: "calc(100dvh - 260px)", sm: "calc(92dvh - 245px)" },
                }}
              >
                <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: { xs: 0, md: 980 } }}>
                  <TableHead>
                    <TableRow sx={{ "& .MuiTableCell-root": { bgcolor: "var(--wp-panel-surface, #fff)", borderColor: ERP_BRAND.border, color: ERP_BRAND.muted, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".04em" } }}>
                      <TableCell sx={{ pl: { xs: 0.6, sm: 1.25 } }}>Ítem</TableCell>
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" }, width: 210 }}>Variante</TableCell>
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" }, width: 145 }}>SKU / código</TableCell>
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" }, width: 160 }}>Categoría</TableCell>
                      <TableCell align="center" sx={{ display: { xs: "none", sm: "table-cell" }, width: 110 }}>Estado</TableCell>
                      <TableCell align="right" sx={{ width: { xs: 164, sm: 230 }, pr: { xs: 0.5, sm: 1.25 } }}>Cantidad real</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && !lines.length ? (
                      <TableRow><TableCell colSpan={6} sx={{ py: 6, textAlign: "center" }}><WebProfitSpinner /></TableCell></TableRow>
                    ) : null}
                    {!loading && !lines.length ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 6, textAlign: "center", color: ERP_BRAND.muted }}>
                          No hay ítems para estos filtros.
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {lines.map((line, index) => (
                      <CountLineRow
                        key={line.id}
                        line={line}
                        inputValue={quantityInputs[line.id] ?? quantityText(line.countedQuantity)}
                        inputRef={(node) => { if (node) inputRefs.current.set(line.id, node); else inputRefs.current.delete(line.id); }}
                        onInputChange={(raw) => inputChange(line, raw)}
                        onInputBlur={() => {
                          if ((quantityInputs[line.id] ?? "") === "") {
                            setQuantityInputs((current) => ({ ...current, [line.id]: quantityText(line.countedQuantity) }));
                          }
                        }}
                        onIncrement={() => changeBy(line, 1)}
                        onDecrement={() => changeBy(line, -1)}
                        onPending={() => setLineLocal(line.id, null)}
                        onNext={() => goNext(index)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <CountPagination
                page={page}
                total={totalFiltered}
                loading={loading}
                onPageChange={(nextPage) => { focusFirstRef.current = true; setPage(nextPage); }}
              />
            </>
          ) : null}

          {mode === "review" ? (
            <>
              {session?.status === "confirming" ? (
                <Alert severity="warning">La confirmación anterior quedó en recuperación. Reintentá con esta misma sesión; WebProfit reutiliza la misma clave y no duplica ajustes.</Alert>
              ) : null}
              <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                <Chip label={`${review?.summary?.counted ?? summary?.counted ?? 0} contados`} color="primary" />
                <Chip label={`${review?.summary?.pending ?? summary?.pending ?? 0} pendientes`} />
                <Chip label={`${review?.summary?.increase || 0} suben`} color="success" variant="outlined" />
                <Chip label={`${review?.summary?.decrease || 0} bajan`} color="warning" variant="outlined" />
                <Chip label={`${review?.summary?.unchanged || 0} sin diferencia`} variant="outlined" />
                <Chip label={`${review?.summary?.zeros || 0} en cero`} variant="outlined" />
              </Stack>
              {(review?.summary?.movedAfterCount || 0) > 0 ? <Alert severity="info">Hubo movimientos posteriores en {review.summary.movedAfterCount} líneas. Se conservarán: se aplicará sólo la diferencia observada al contar.</Alert> : null}
              {(review?.summary?.blocking || 0) > 0 ? <Alert severity="error">Hay {review.summary.blocking} líneas que deben dejarse pendientes o volver a contarse antes de confirmar.</Alert> : null}
              <Stack direction="row" spacing={0.7}>
                <Button variant={reviewScope === "differences" ? "contained" : "outlined"} onClick={() => { setReviewScope("differences"); setReviewPage(1); }} sx={{ textTransform: "none" }}>Diferencias</Button>
                <Button variant={reviewScope === "all" ? "contained" : "outlined"} onClick={() => { setReviewScope("all"); setReviewPage(1); }} sx={{ textTransform: "none" }}>Ver todo</Button>
              </Stack>
              {review?.migrationReconciliation?.available ? (
                <Box sx={{ border: `1px solid ${ERP_BRAND.border}`, bgcolor: "var(--wp-panel-surface, #fff)", borderRadius: 3, p: 1.4 }}>
                  <Stack spacing={0.8}>
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Cierre de la historia anterior</Typography>
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                        El conteo físico va a ser tu nuevo punto de partida. La historia sirve para comparar; las diferencias no se transforman en ventas inventadas.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                      <Chip size="small" label={`${review.migrationReconciliation.historicalDocuments} documentos históricos`} />
                      <Chip size="small" label={`${review.migrationReconciliation.linesWithHistory} existencias comparables`} />
                      {review.migrationReconciliation.linesWithUnexplainedOutflow ? <Chip size="small" variant="outlined" label={`${review.migrationReconciliation.linesWithUnexplainedOutflow} con salida sin explicar`} /> : null}
                    </Stack>
                    {review.migrationReconciliation.primaryCurrency ? (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.2, sm: 2 }}>
                        <Typography variant="body2">Compras históricas: <b>{money(review.migrationReconciliation.historicalPurchaseAmount, review.migrationReconciliation.primaryCurrency)}</b></Typography>
                        {Number(review.migrationReconciliation.historicalSaleDocuments || 0) > 0 ? <Typography variant="body2">Ventas históricas registradas: <b>{money(review.migrationReconciliation.historicalKnownSalesAmount, review.migrationReconciliation.primaryCurrency)}</b></Typography> : null}
                        <Typography variant="body2">Stock contado a costo histórico: <b>{money(review.migrationReconciliation.estimatedOpeningInventoryValueAtHistoricalCost, review.migrationReconciliation.primaryCurrency)}</b></Typography>
                        <Typography variant="body2">Salida no explicada a costo: <b>{money(review.migrationReconciliation.estimatedUnexplainedOutflowAtHistoricalCost, review.migrationReconciliation.primaryCurrency)}</b></Typography>
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>
              ) : null}
              {loading && !review ? <Stack alignItems="center" sx={{ py: 4 }}><WebProfitSpinner /></Stack> : null}
              {review && !review.lines.length ? <Alert severity="success">El stock contado coincide; no hay diferencias para revisar.</Alert> : null}
              <Stack spacing={1}>
                {(review?.lines || []).map((line) => <ReviewRow key={line.id} line={line} onOmit={() => omitReviewLine(line)} disabled={loading || confirming} />)}
              </Stack>
              {review?.totalFiltered > review?.pageSize ? (
                <Stack direction="row" justifyContent="space-between">
                  <Button disabled={reviewPage <= 1 || loading} onClick={() => setReviewPage((value) => Math.max(1, value - 1))}>Anterior</Button>
                  <Typography variant="caption" sx={{ alignSelf: "center", color: ERP_BRAND.muted }}>Página {reviewPage}</Typography>
                  <Button disabled={!review?.hasMore || loading} onClick={() => setReviewPage((value) => value + 1)}>Siguiente</Button>
                </Stack>
              ) : null}
              {!canConfirm ? <Alert severity="info">El conteo está listo. Un usuario con permiso para confirmar movimientos debe aplicarlo.</Alert> : null}
              {canConfirm && (review?.summary?.pending || 0) > 0 ? (
                <Alert severity="warning">
                  Quedan pendientes. Lo recomendado es volver a contarlos. Si finalizás sólo lo contado, esos ítems conservarán su stock actual y no recibirán ajustes.
                </Alert>
              ) : null}
            </>
          ) : null}

          {mode === "result" && result ? (
            <Stack spacing={2} alignItems="center" textAlign="center" sx={{ py: { xs: 3, sm: 5 } }}>
              <CheckCircleRoundedIcon color="success" sx={{ fontSize: 62 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{result.migrationReconciliation?.available ? "Puesta a punto cerrada" : "Conteo confirmado"}</Typography>
                <Typography sx={{ color: ERP_BRAND.muted, mt: 0.7 }}>
                  {result.noAdjustmentsNeeded ? "El stock ya coincidía; no fue necesario aplicar ajustes." : `Se aplicaron ${result.adjusted} correcciones de stock.`}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" justifyContent="center">
                <Chip label={`${result.unchanged || 0} sin cambios`} />
                {result.pendingOmitted ? <Chip label={`${result.pendingOmitted} pendientes omitidos`} /> : null}
                {result.movementNumber ? <Chip label={`Movimiento ${result.movementNumber}`} variant="outlined" /> : null}
              </Stack>
              {result.migrationReconciliation?.available ? (
                <Box sx={{ width: "100%", maxWidth: 720, border: `1px solid ${ERP_BRAND.border}`, bgcolor: "var(--wp-panel-surface, #fff)", borderRadius: 3, p: { xs: 1.4, sm: 1.7 }, textAlign: "left" }}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Puesta a punto conciliada</Typography>
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                        Desde ahora manda el stock que acabás de contar. Los comprobantes anteriores quedan como referencia para entender de dónde venía la mercadería.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                      <Chip size="small" label={`${result.migrationReconciliation.historicalPurchaseDocuments || 0} compras históricas`} />
                      <Chip size="small" label={`${result.migrationReconciliation.historicalSaleDocuments || 0} ventas históricas registradas`} />
                      <Chip size="small" label={`${result.migrationReconciliation.linesWithHistory || 0} existencias conciliadas`} />
                    </Stack>
                    {result.migrationReconciliation.primaryCurrency ? (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.3, sm: 2.2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Compras históricas cargadas</Typography>
                          <Typography sx={{ fontWeight: 950 }}>{money(result.migrationReconciliation.historicalPurchaseAmount, result.migrationReconciliation.primaryCurrency)}</Typography>
                        </Box>
                        {Number(result.migrationReconciliation.historicalSaleDocuments || 0) > 0 ? (
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Ventas históricas realmente registradas</Typography>
                            <Typography sx={{ fontWeight: 950 }}>{money(result.migrationReconciliation.historicalKnownSalesAmount, result.migrationReconciliation.primaryCurrency)}</Typography>
                          </Box>
                        ) : null}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Stock inicial estimado a costo histórico</Typography>
                          <Typography sx={{ fontWeight: 950 }}>{money(result.migrationReconciliation.estimatedOpeningInventoryValueAtHistoricalCost, result.migrationReconciliation.primaryCurrency)}</Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Salida histórica sin explicar · a costo</Typography>
                          <Typography sx={{ fontWeight: 950 }}>{money(result.migrationReconciliation.estimatedUnexplainedOutflowAtHistoricalCost, result.migrationReconciliation.primaryCurrency)}</Typography>
                        </Box>
                      </Stack>
                    ) : (
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Hay documentos en más de una moneda; WebProfit no mezcla esos importes en una estimación monetaria única.</Typography>
                    )}
                    <Alert severity="info" sx={{ py: 0 }}>
                      “Salida sin explicar” es una estimación: puede incluir ventas no cargadas, consumo, merma o historia faltante. No genera ventas, ingresos, impuestos ni caja.
                    </Alert>
                  </Stack>
                </Box>
              ) : null}
              <Button variant="outlined" onClick={onGoMovements} sx={{ textTransform: "none", fontWeight: 900 }}>Ver movimientos de stock</Button>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      {mode === "work" ? (
        <DialogActions sx={{ px: { xs: 1.25, sm: 2.5 }, py: 1.2, borderTop: `1px solid ${ERP_BRAND.border}`, justifyContent: "space-between" }}>
          <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={discard} disabled={loading || pendingSaveCount > 0} sx={{ textTransform: "none" }}>Descartar</Button>
          <Button variant="contained" onClick={async () => { const saved = await flushAll(); if (!saved) { setError("Hay cambios que todavía no pudieron guardarse. Reintentá antes de revisar."); return; } setReviewPage(1); setMode("review"); }} disabled={loading || Object.keys(saveErrors).length > 0} sx={{ minHeight: 44, textTransform: "none", fontWeight: 950 }}>Revisar conteo</Button>
        </DialogActions>
      ) : null}
      {mode === "review" ? (
        <DialogActions sx={{ px: { xs: 1.25, sm: 2.5 }, py: 1.2, borderTop: `1px solid ${ERP_BRAND.border}`, flexWrap: "wrap" }}>
          {session?.status !== "confirming" ? <Button onClick={() => setMode("work")} sx={{ mr: "auto", textTransform: "none" }}>Volver a pendientes</Button> : null}
          {canConfirm && (review?.summary?.blocking || 0) === 0 && (review?.summary?.pending || 0) > 0 ? (
            <Button variant="contained" onClick={() => confirm(true)} disabled={confirming || loading} sx={{ textTransform: "none", fontWeight: 950 }}>
              {confirming ? "Confirmando…" : session?.status === "confirming" ? "Reintentar confirmación" : "Finalizar solo lo contado"}
            </Button>
          ) : null}
          {canConfirm && (review?.summary?.blocking || 0) === 0 && (review?.summary?.pending || 0) === 0 ? (
            <Button variant="contained" onClick={() => confirm(false)} disabled={confirming || loading} sx={{ textTransform: "none", fontWeight: 950 }}>
              {confirming ? "Confirmando…" : session?.status === "confirming" ? "Reintentar confirmación" : "Confirmar conteo"}
            </Button>
          ) : null}
        </DialogActions>
      ) : null}
      {mode === "result" ? (
        <DialogActions sx={{ px: 2.5, py: 1.2, borderTop: `1px solid ${ERP_BRAND.border}` }}>
          <Button variant="contained" onClick={onClose} sx={{ textTransform: "none", fontWeight: 950 }}>Listo</Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}
